import { Divider, Typography } from "@material-ui/core"
import { AddCircle } from "@material-ui/icons"
import PropTypes from "prop-types"
import React, { useContext, useEffect } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { useQuery } from "react-query"
import { listCourses } from "~/actions/courses"
import DarkButton from "~/components/Common/DarkButton"
import AppContext from "~/contexts/App"
import CourseContext from "~/contexts/Course"
import AddAssessmentDialog from "~/dialogs/AddAssesmentDialog"
import AddCourseDialogGeneric from "~/dialogs/AddCourseDialogGeneric"
import AddCourseCard from "~/dialogs/AddCourseDialogGeneric/CourseCard"
import AddLabDialog from "~/dialogs/AddLabDialog"
import AddLessonDialog from "~/dialogs/AddLessonDialog"

import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import Box from "../../Common/Box"
import AddedAssessment from "../AddedAssessment"
import AddedLab from "../AddedLab"
import AddedLessonCard from "../AddedLessonCard"
import NoFinalAssessment from "../NoFinalAssessment"
import NoLabAdded from "../NoLabAdded"
import NoLessonAdded from "../NoLessonAdded"
import useStyles from "./styles"

//function to reorder the lessons
//it is using a react library called react-beautiful-dnd
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

function Lesson({ lesson, index, disabledCourse }) {
  return (
    <Draggable draggableId={lesson.lesson_id.toString()} index={index}>
      {(provided) => (
        <AddedLessonCard
          lesson={lesson}
          provided={provided}
          disabledCourse={disabledCourse}
        />
      )}
    </Draggable>
  )
}

const LessonList = React.memo(function LessonList({ lessons, disabledCourse }) {
  return lessons.map((lesson, index) => (
    <Lesson
      lesson={lesson}
      index={index}
      key={lesson.lesson_id}
      disabledCourse={disabledCourse}
    />
  ))
})

function NextCourse({ course, addedCourses, setAddedCourses, index }) {
  return (
    <Draggable draggableId={course.id.toString()} index={index}>
      {(provided) => {
        return (
          <AddCourseCard
            course={course}
            type="view"
            index={index}
            addedCourses={addedCourses}
            setAddedCourses={setAddedCourses}
            provided={provided}
          />
        )
      }}
    </Draggable>
  )
}

const NextCourseList = React.memo(function NextCourseList({
  courses,
  setAddedCourses,
}) {
  return courses?.map((course, index) => (
    <NextCourse
      course={course}
      type="view"
      index={index}
      key={course.id}
      addedCourses={courses}
      setAddedCourses={setAddedCourses}
    />
  ))
})

const CourseContent = () => {
  const classes = useStyles()
  const { openDialog } = useContext(AppContext)
  const {
    course,
    lessonsArray,
    setLessonsArray,
    setInitialLessonsArray,
    nextCourses,
    setNextCourses,
    setInitialNextCourses,
  } = useContext(CourseContext)

  const { data: courses } = useQuery("courses", () => listCourses())

  const openAddLessonDialog = () => {
    let data = { course: course }
    openDialog("addLessonDialog", data)
  }

  const openAddAssessmentDialog = () => {
    let data = { courseId: course.id }
    if (course?.published === 1) {
      data = { courseId: course.id, lockdown: true }
    }
    openDialog("addAssessmentDialog", data)
  }

  const openAddLabDialog = () => {
    openDialog("addLabDialog", course)
  }

  //populate lessons array
  useEffect(() => {
    let newArray = []
    course?.lessons?.map((x) => {
      newArray.push({ lesson_id: x.id, data: x })
    })
    setLessonsArray(newArray)
    setInitialLessonsArray(newArray)
  }, [course.lessons])

  useEffect(() => {
    if (courses && courses.length > 0 && course?.next_courses.length > 0) {
      const nextCourses = course.next_courses.map((nc) =>
        courses.find((c) => c.id === nc.id),
      )
      setNextCourses(nextCourses)
      setInitialNextCourses(nextCourses)
    }
  }, [course?.next_courses, courses])

  function onDragEnd(result) {
    if (!result.destination) {
      return
    }

    if (result.destination.index === result.source.index) {
      return
    }

    const lessons = reorder(
      lessonsArray,
      result.source.index,
      result.destination.index,
    )

    setLessonsArray(lessons)
  }

  const disabledCourse =
    (course.published === 1 || course.has_been_published === 1) &&
    course.archived === 0

  const renderFinalAssessmentOptions = () => {
    if (course?.assessment_id !== null) {
      return (
        <AddedAssessment
          assessment={course?.assessment}
          onClick={openAddAssessmentDialog}
        />
      )
    } else if (course.disable_final_assessment) {
      return (
        <Typography variant="subtitle1" className={classes.subtitle}>
          Final Assessment Disabled in Course Settings
        </Typography>
      )
    }
    return <NoFinalAssessment onClick={openAddAssessmentDialog} />
  }

  const renderNextCourses = () => {
    function onNextCoursesDragEnd(result) {
      if (!result.destination) {
        return
      }

      if (result.destination.index === result.source.index) {
        return
      }

      const orderedNextCourses = reorder(
        nextCourses,
        result.source.index,
        result.destination.index,
      )

      setNextCourses(orderedNextCourses)
    }

    const addNextCourses = async () => {
      const newCourses = await openDialog("addCourseDialogGeneric", nextCourses)
      if (newCourses !== null) {
        setNextCourses(newCourses)
      }
    }

    return (
      <>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="10px"
          zIndex={10}
        >
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", marginRight: "8px" }}
            >
              Next Courses{" "}
            </Typography>
            <Typography
              variant="h3"
              color="primary"
              className={classes.orderText}
            >
              In order as they appear
            </Typography>
          </Box>
          <PermissionsWrapper permissionGroup="admin">
            <DarkButton
              onClick={addNextCourses}
              // style={path?.published ? { display: "none" } : null}
              startIcon={
                <AddCircle
                  fontSize="small"
                  classes={{ root: classes.addCircle }}
                />
              }
            >
              Add Course
            </DarkButton>
          </PermissionsWrapper>
        </Box>
        <Box marginLeft={"37px"} marginRight="37px" paddingBottom="32px">
          {nextCourses?.length > 0 ? (
            <DragDropContext onDragEnd={onNextCoursesDragEnd}>
              <Droppable droppableId="course">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <NextCourseList
                      courses={nextCourses}
                      setAddedCourses={setNextCourses}
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : null}
        </Box>
        <AddCourseDialogGeneric />
      </>
    )
  }

  return (
    <Box>
      <Box position="relative" minHeight="100%">
        {disabledCourse ? (
          <Box className={classes.publishedBanner}>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              {course?.published === 1 ? (
                <Typography className={classes.title} variant="h3">
                  You are in a published course, this cannot be updated
                </Typography>
              ) : null}
              {course?.published === 0 && course?.has_been_published === 1 ? (
                <Typography className={classes.title} variant="h3">
                  You are in a unpublished course, this cannot be updated
                </Typography>
              ) : null}
              <Typography variant="subtitle1" className={classes.subtitle}>
                To update this you must create a new version
              </Typography>
            </Box>
          </Box>
        ) : null}
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="10px"
        >
          <Box display="flex" flexDirection="row" minHeight="37px">
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", zIndex: 4 }}
            >
              Lab
            </Typography>
          </Box>
        </Box>
        <Box marginLeft={"37px"} marginRight="37px" paddingBottom="16px">
          {course?.lab_id !== null ? (
            <AddedLab
              courseId={course?.id}
              lab={course?.lab}
              disabled={disabledCourse}
            />
          ) : (
            <NoLabAdded onClick={openAddLabDialog} />
          )}
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="10px"
        >
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            minHeight="37px"
          >
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", marginRight: "8px" }}
            >
              Lessons{" "}
            </Typography>
            <Typography
              variant="h3"
              color="primary"
              className={classes.orderText}
            >
              In order as they appear
            </Typography>
          </Box>
          {lessonsArray?.length > 0 && (
            <PermissionsWrapper permissionGroup="admin">
              <DarkButton
                onClick={openAddLessonDialog}
                startIcon={
                  <AddCircle
                    fontSize="small"
                    classes={{ root: classes.addCircle }}
                  />
                }
                style={course?.published ? { display: "none" } : null}
              >
                Add Lesson
              </DarkButton>
            </PermissionsWrapper>
          )}
        </Box>
        <Box marginLeft={"37px"} marginRight="37px">
          {lessonsArray?.length > 0 ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="list">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <LessonList
                      lessons={lessonsArray}
                      disabledCourse={disabledCourse}
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <NoLessonAdded onClick={openAddLessonDialog} />
          )}
        </Box>
        {disabledCourse ? <Box className={classes.publishedOverlay} /> : null}

        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="10px"
        >
          <Box display="flex" flexDirection="row" minHeight="37px">
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", zIndex: 4 }}
            >
              Final Assessment
            </Typography>
          </Box>
        </Box>
        {course?.disable_final_assessment ? (
          <Box marginLeft={"37px"}>
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: "500",
                zIndex: 4,
                fontStyle: "italic",
                fontSize: 16,
              }}
            >
              Final Assessment Disabled
            </Typography>
          </Box>
        ) : null}
        {!course?.disable_final_assessment && (
          <Box marginLeft={"37px"} marginRight="37px" paddingBottom="16px">
            {renderFinalAssessmentOptions()}
          </Box>
        )}
      </Box>

      <Divider
        style={{
          backgroundColor: "#000",
          opacity: "0.1",
          marginTop: 35,
        }}
      />
      {renderNextCourses()}

      <AddLessonDialog />
      <AddAssessmentDialog />
      <AddLabDialog />
    </Box>
  )
}

Lesson.propTypes = {
  lesson: PropTypes.object,
  index: PropTypes.number,
  disabledCourse: PropTypes.bool,
}

LessonList.propTypes = {
  lessons: PropTypes.array,
  disabledCourse: PropTypes.bool,
}

NextCourse.propTypes = {
  course: PropTypes.object,
  index: PropTypes.number,
  addedCourses: PropTypes.array,
  setAddedCourses: PropTypes.func,
}

NextCourseList.propTypes = {
  courses: PropTypes.array,
  setAddedCourses: PropTypes.func,
}

CourseContent.propTypes = {
  lesson: PropTypes.object,
  index: PropTypes.number,
  "lesson.id": PropTypes.string,
  disabledCourse: PropTypes.bool,
}
export default CourseContent
